import { differenceInHours } from 'date-fns';
import React from 'react';
import { useIntl } from 'react-intl';

import { User } from '~/components/Shared/User/User';
import { DateText } from '~/eds/src/components/DateText/DateText';
import { TicketActivityType } from '~/enums/TicketActivityType';
import { externalUserToUser } from '~/utils/user';

interface Props {
  lastModifiedById: number | null;
  lastModifiedByEmail: string | null;
  modifiedDate: string | null;
  hasTicketInboundEmail: boolean;
  firstActivityAction: string | null;
}

const LastModifiedBy = ({
  lastModifiedById,
  lastModifiedByEmail,
  modifiedDate,
  hasTicketInboundEmail,
  firstActivityAction,
}: Props) => {
  const intl = useIntl();

  const getUserName = () => {
    if (lastModifiedById) {
      return <User id={lastModifiedById} mode="name" />;
    }
    if (lastModifiedByEmail) {
      return (
        <User
          user={externalUserToUser({ email: lastModifiedByEmail })}
          mode="name"
        />
      );
    }
    return null;
  };

  const userName = getUserName();
  const counterpartyEmail = lastModifiedByEmail;
  const submittedByEmailIntake =
    hasTicketInboundEmail &&
    firstActivityAction === TicketActivityType.InboundAttachmentAccepted;

  const isRecentlyModified =
    modifiedDate && differenceInHours(new Date(), new Date(modifiedDate)) <= 35;

  const lastEditedUser =
    submittedByEmailIntake && counterpartyEmail ? counterpartyEmail : userName;

  if (isRecentlyModified) {
    return intl.formatMessage(
      {
        id: 'TicketsViewPage.lastEditedLessThan35Hours',
        defaultMessage:
          ' • Last modified {lastModifiedRelativeDate} by {lastEditedUser}',
        description:
          'Displays the last modified time and the user who last modified the document. ie: Last modified 5 minutes ago by John Doe',
      },
      {
        lastModifiedRelativeDate: (
          <DateText
            date={modifiedDate ? new Date(modifiedDate) : undefined}
            format="duration"
            enableTick
          />
        ),
        lastEditedUser,
      },
    );
  }

  return intl.formatMessage(
    {
      id: 'TicketsViewPage.lastEditedMoreThan35Hours',
      defaultMessage:
        ' • Last modified {lastModifiedRelativeDate} by {lastEditedUser}',
      description:
        'Displays the last modified time after more than 35 hours and the user who last modified the document. ie: Last modified January 1, 2025, 12:00 PM by John Doe',
    },
    {
      lastModifiedRelativeDate: (
        <DateText
          date={modifiedDate ? new Date(modifiedDate) : undefined}
          format="duration"
          enableTick
        />
      ),
      lastEditedUser,
    },
  );
};

export default LastModifiedBy;
